import React from 'react';
import { Button, Form, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CustomMarkerChartType,
  CustomMarkerDefinition,
  CustomMarkerRanges,
  CustomMarkerResultType,
  CUSTOM_MARKER_CHART_TYPE_OPTIONS,
  CUSTOM_MARKER_RESULT_SHAPE_OPTIONS,
  CUSTOM_MARKER_EXPECTED_MAX_LENGTH,
  isTextResult,
  resultShapeValue,
  resultShapeFields,
  CUSTOM_MARKER_DEFAULT_PRECISION,
  CUSTOM_MARKER_OWN_SECTION_LABEL,
  CUSTOM_MARKER_RANGE_KEYS,
  assignableSection,
  PanelField,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  shippedMarkerNamed,
  practitionerSectionOptionValue,
  sectionOptionValueFor,
  MARKER_NAME_PLACEHOLDER_EXAMPLE,
  MARKER_UNITS_PLACEHOLDER_EXAMPLE,
  MARKER_NAME_PLACEHOLDER_TEXT_EXAMPLE,
  CustomMarker,
  CustomMarkerRangeKey,
  CustomMarkerSection,
  CustomMarkerSectionGroup,
  Gender,
  groupBySectionValue,
  isDefaultTestMarker,
  isDefaultTestSection,
  isExpandedTestSection,
  sectionDisplayLabel,
  sectionGroupKey,
} from '../../models';
import {
  ChartTypeHelp,
  CustomMarkerRangeStrings,
  RequiredMark,
  SectionSelect,
  derivedRangeValue,
  isCalculatedRangeKey,
  moveRowWithin,
  validateCustomMarkerRangeStrings,
} from '../bloodworkz/custom-markers-table';
import '../bloodworkz/custom-markers-table.css';

/**
 * A library row while it is being edited. Held as strings for the same reasons
 * the per-test table is: a half-typed "4." must survive a re-render, and the
 * decimals TYPED are what set the marker's precision.
 */
export type CustomMarkerDefinitionRow = {
  id: string;
  name: string;
  units: string;
  /** Numeric or text — see CustomMarkerResultType. */
  resultType: CustomMarkerResultType;
  /**
   * The expected wording for a text marker, remembered so a panel defined once
   * carries its expectations onto every test. The RESULT is never held here:
   * that is what changes per test.
   */
  expectedText: string;
  chartType: CustomMarkerChartType;
  /**
   * Remembered section placement, '' for the marker's own section. Copied onto
   * every test the definition is picked onto, where it can still be changed for
   * that one test.
   */
  section: string;
  ranges: CustomMarkerRangeStrings;
  /** Per-sex overrides. Present in the row only while the box is checked. */
  female?: CustomMarkerRangeStrings;
  male?: CustomMarkerRangeStrings;
};

const GENDER_VARIANTS: { key: 'female' | 'male'; gender: Gender; label: string }[] = [
  { key: 'female', gender: Gender.FEMALE, label: 'Female' },
  { key: 'male', gender: Gender.MALE, label: 'Male' },
];

export function emptyRangeStrings(): CustomMarkerRangeStrings {
  return {
    alarmMin: '',
    labMin: '',
    optimalMin: '',
    optimalMax: '',
    labMax: '',
    alarmMax: '',
  };
}

const asText = (value: any): string =>
  value === undefined || value === null ? '' : String(value);

const rangeStringsFrom = (ranges?: CustomMarkerRanges): CustomMarkerRangeStrings => {
  const strings = emptyRangeStrings();
  CUSTOM_MARKER_RANGE_KEYS.forEach(key => {
    strings[key] = asText(ranges ? ranges[key] : undefined);
  });
  return strings;
};

const rangesFrom = (strings: CustomMarkerRangeStrings): CustomMarkerRanges => {
  const ranges: CustomMarkerRanges = {};
  CUSTOM_MARKER_RANGE_KEYS.forEach(key => {
    const text = (strings[key] || '').trim();
    if (!text) {
      return;
    }
    const parsed = Number(text);
    if (!isNaN(parsed)) {
      ranges[key] = parsed;
    }
  });
  return ranges;
};

/**
 * Decimal places for a definition: the most typed across its lab bounds, in any
 * range set. Never undefined — MathUtils.round(x, undefined) is NaN.
 */
export function precisionForDefinitionRow(row: CustomMarkerDefinitionRow): number {
  const typed = [row.ranges, row.female, row.male]
    .filter(Boolean)
    .flatMap(set => [set!.labMin, set!.labMax])
    .filter(text => (text || '').trim());

  if (!typed.length) {
    return CUSTOM_MARKER_DEFAULT_PRECISION;
  }
  const decimals = typed.map(text => {
    const dot = text.indexOf('.');
    return dot < 0 ? 0 : text.slice(dot + 1).replace(/[^0-9]/g, '').length;
  });
  return Math.min(3, Math.max(0, ...decimals));
}

/**
 * True when a range set carries at least one lab bound — i.e. it is a real
 * range rather than a half-filled form. The same bar `resolveDefinitionRanges`
 * uses to decide whether a variant overrides the default.
 */
export function hasLabBound(strings?: CustomMarkerRangeStrings): boolean {
  if (!strings) {
    return false;
  }
  return !!(strings.labMin || '').trim() || !!(strings.labMax || '').trim();
}

/**
 * True when the default range still holds values that no longer cover anybody.
 *
 * Once BOTH sexes carry their own lab bounds every client is read against a
 * variant, so the numbers left in the top row are read by nothing — the same
 * condition `definitionRowProblems` already stops asking about, and the reason
 * the panel's note says the ranges above can stay empty. A practitioner who
 * builds a marker by filling the default first and splitting it by sex second
 * is left holding them, which is what the clear action is for.
 *
 * Deliberately NOT `row.female || row.male`: with one sex set the default is
 * still the fallback for the other, and clearing it there would leave that half
 * of the practitioner's clients with no range at all.
 */
export function defaultRangeIsRedundant(row: CustomMarkerDefinitionRow): boolean {
  const everySexCovered = GENDER_VARIANTS.every(({ key }) => hasLabBound(row[key]));
  const defaultTyped = CUSTOM_MARKER_RANGE_KEYS.some(key =>
    (row.ranges[key] || '').trim(),
  );
  return everySexCovered && defaultTyped;
}

/**
 * A row nobody has typed anything into.
 *
 * Judged on the fields the row is CURRENTLY SHOWING, not on everything it
 * carries. Now that both shapes' fields are kept (see libraryFromRows), a row
 * holds values whose inputs are not rendered, and counting those made a row
 * that looks empty impossible to satisfy: type an Expected Result, switch back
 * to a chart shape, and Save reported "enter a marker name, or clear the row"
 * against a row with nothing on it to clear, because the field holding it up
 * was no longer on screen.
 */
export function isBlankDefinitionRow(row: CustomMarkerDefinitionRow): boolean {
  if (row.name.trim()) {
    return false;
  }
  return isTextResult(row)
    ? !(row.expectedText || '').trim()
    : !row.units.trim() &&
        CUSTOM_MARKER_RANGE_KEYS.every(key => !(row.ranges[key] || '').trim());
}

export function emptyDefinitionRow(
  id: string,
  resultType: CustomMarkerResultType = CustomMarkerResultType.NUMERIC,
): CustomMarkerDefinitionRow {
  return {
    id,
    name: '',
    units: '',
    resultType,
    expectedText: '',
    chartType: CustomMarkerChartType.STANDARD,
    section: '',
    ranges: emptyRangeStrings(),
  };
}

export function rowsFromLibrary(
  library?: CustomMarkerDefinition[],
): CustomMarkerDefinitionRow[] {
  return (library || []).map(definition => {
    const byGender = definition.rangesByGender || {};
    const female = byGender[Gender.FEMALE];
    const male = byGender[Gender.MALE];
    return {
      id: definition.id,
      name: asText(definition.name),
      units: asText(definition.units),
      resultType: isTextResult(definition)
        ? CustomMarkerResultType.TEXT
        : CustomMarkerResultType.NUMERIC,
      expectedText: asText(definition.expectedText),
      chartType: definition.chartType || CustomMarkerChartType.STANDARD,
      // A section retired since the definition was saved reads as blank, so the
      // dropdown shows the fallback the report actually uses.
      section: sectionOptionValueFor(definition),
      ranges: rangeStringsFrom(definition.ranges),
      female: female ? rangeStringsFrom(female) : undefined,
      male: male ? rangeStringsFrom(male) : undefined,
    };
  });
}

export function libraryFromRows(
  rows: CustomMarkerDefinitionRow[],
): CustomMarkerDefinition[] {
  return (rows || [])
    .filter(row => !isBlankDefinitionRow(row))
    .map(row => {
      const isText = isTextResult(row);
      // Everything the row carries is stored, whichever shape it is in. The
      // fields the other shape uses lie DORMANT rather than being dropped:
      // nothing reads a range off a text marker (customMarkerFromDefinition
      // branches before it copies one, and populateCustomPanelMarkers never
      // sees a text marker at all), so keeping them costs a few attributes and
      // buys the one thing the practitioner expects.
      //
      // They used to be discarded. Picking "Text marker (no chart)" on a saved
      // marker and saving destroyed its units, its whole reference range and
      // every per-sex variant, with no confirmation and no way back: switching
      // the dropdown again returned a marker with twelve empty fields. That
      // also made resultShapeFields' promise that a marker "switched by mistake
      // and switched back is charted the way it was" true only until Save.
      const definition = new CustomMarkerDefinition({
        id: row.id,
        name: row.name.trim(),
        chartType: row.chartType || CustomMarkerChartType.STANDARD,
        precision: precisionForDefinitionRow(row),
        ranges: rangesFrom(row.ranges),
      });
      if (isText) {
        definition.resultType = CustomMarkerResultType.TEXT;
      }
      // Empty strings are never persisted — DynamoDB rejects them.
      const expected = (row.expectedText || '').trim();
      if (expected) {
        definition.expectedText = expected;
      }
      const units = row.units.trim();
      if (units) {
        definition.units = units;
      }
      const section = assignableSection(row.section);
      if (section) {
        definition.section = section;
      } else {
        const practitionerSection = practitionerSectionFromOptionValue(
          row.section,
        );
        if (practitionerSection) {
          definition.practitionerSection = practitionerSection;
        }
      }

      // Kept on a text row too. A per-sex override is dormant there, exactly
      // as the default range is, and dropping it here was the other half of
      // the loss above: the variants went even though the row still showed
      // them the moment the dropdown was switched back.
      const byGender: { [gender: string]: CustomMarkerRanges } = {};
      GENDER_VARIANTS.forEach(({ key, gender }) => {
        const strings = row[key];
        if (!strings) {
          return;
        }
        const ranges = rangesFrom(strings);
        // A variant with no lab bounds is a half-filled form, not an override.
        if (ranges.labMin != null || ranges.labMax != null) {
          byGender[gender] = ranges;
        }
      });
      if (Object.keys(byGender).length) {
        definition.rangesByGender = byGender;
      }
      return definition;
    });
}

/** What is wrong with one row, and which row it is. */
export type DefinitionRowProblem = {
  index: number;
  key: string;
  /** Bare message, no marker name: it is rendered against the row itself. */
  message: string;
};

/**
 * The rows whose name is already taken by an earlier row.
 *
 * Split out from the rest of validation because it is the one rule this page
 * checks as it is TYPED rather than at Save: a half-filled range is the normal
 * state of a form, but a name that is already in the library is wrong the
 * moment it is complete, and the marker it clashes with is usually folded away
 * in another section where nobody can see it. The rest of the rules stay on
 * Save, where they read as an answer rather than as nagging.
 */
export function duplicateNameProblems(
  rows: CustomMarkerDefinitionRow[],
): DefinitionRowProblem[] {
  const problems: DefinitionRowProblem[] = [];
  const seenNames = new Map<string, number>();

  (rows || []).forEach((row, index) => {
    // A row nobody has typed into is not a marker yet, and two of them are not
    // two markers with the same name.
    if (isBlankDefinitionRow(row)) {
      return;
    }
    const name = row.name.trim();
    if (!name) {
      return;
    }
    const nameKey = CustomMarker.keyFor(name);
    const first = seenNames.get(nameKey);
    if (first !== undefined) {
      problems.push({
        index,
        key: `libraryMarker${index}duplicate`,
        // Names its own subject: this is read under the row it is about, where
        // there is no marker name in front of it to carry the sentence. The
        // twin is placed by its SECTION rather than by a row number, because
        // the list is drawn in section groups and nothing on the page is
        // numbered for a practitioner to count to.
        message: `This name is already in My Markers, under ${sectionDisplayLabel(
          rows[first].section,
        )}. Use a different name.`,
      });
      return;
    }
    seenNames.set(nameKey, index);
  });

  return problems;
}

/**
 * Library validation. Mirrors the per-test rules through the shared
 * validateCustomMarkerRangeStrings, minus the Value requirement (a definition
 * has no result), plus name uniqueness across the library.
 *
 * Reports per ROW rather than as one flat list, because a row that does not
 * validate is now the only thing held back by Save — the rest of the page saves
 * around it, and the message has to be able to find its way to the row it is
 * about.
 */
export function definitionRowProblems(
  rows: CustomMarkerDefinitionRow[],
): DefinitionRowProblem[] {
  const problems: DefinitionRowProblem[] = [];
  // The same rule the page already applies as the name is typed, so Save can
  // never disagree with what the row is showing.
  const duplicates = new Map<number, string>();
  duplicateNameProblems(rows).forEach(({ index, message }) =>
    duplicates.set(index, message),
  );

  (rows || []).forEach((row, index) => {
    if (isBlankDefinitionRow(row)) {
      return;
    }

    const fail = (suffix: string, message: string) =>
      problems.push({ index, key: `libraryMarker${index}${suffix}`, message });

    const name = row.name.trim();
    if (!name) {
      fail('name', 'enter a marker name.');
      return;
    }

    const duplicate = duplicates.get(index);
    if (duplicate) {
      fail('duplicate', duplicate);
      return;
    }

    // A text marker is words: no units to report, no range to check, and its
    // expected result is optional by design.
    if (isTextResult(row)) {
      return;
    }

    const precision = precisionForDefinitionRow(row);

    // Every filled cell, in every range set, must parse.
    const allSets = [row.ranges, row.female, row.male].filter(
      Boolean,
    ) as CustomMarkerRangeStrings[];
    const unparseable = allSets.some(set =>
      CUSTOM_MARKER_RANGE_KEYS.some(key => {
        const text = (set[key] || '').trim();
        return !!text && isNaN(Number(text));
      }),
    );
    if (unparseable) {
      fail('numeric', 'every range entered must be numeric.');
      return;
    }

    // Each enabled sex variant has to stand on its own — it replaces the
    // default outright rather than merging into it.
    for (const { key, label: genderLabel } of GENDER_VARIANTS) {
      const strings = row[key];
      if (!strings) {
        continue;
      }
      const anyTyped = CUSTOM_MARKER_RANGE_KEYS.some(rangeKey =>
        (strings[rangeKey] || '').trim(),
      );
      if (!anyTyped) {
        fail(
          `${key}Empty`,
          `enter lab min and lab max for the ${genderLabel} range, or clear it.`,
        );
        return;
      }
      const variantProblem = validateCustomMarkerRangeStrings(
        strings,
        row.chartType,
        precision,
      );
      if (variantProblem) {
        fail(`${key}${variantProblem.key}`, `${genderLabel} range — ${variantProblem.message}`);
        return;
      }
    }

    // The default range is the fallback for anyone the variants don't cover.
    // Set ranges for BOTH sexes and there is nobody left to fall back on, so
    // the top row can stay empty — a marker whose range is entirely
    // sex-specific should not have to invent a combined one.
    const everySexCovered = GENDER_VARIANTS.every(({ key }) =>
      hasLabBound(row[key]),
    );
    const defaultTyped = CUSTOM_MARKER_RANGE_KEYS.some(rangeKey =>
      (row.ranges[rangeKey] || '').trim(),
    );
    if (everySexCovered && !defaultTyped) {
      return;
    }

    const problem = validateCustomMarkerRangeStrings(
      row.ranges,
      row.chartType,
      precision,
    );
    if (problem) {
      fail(problem.key, problem.message);
    }
  });

  return problems;
}

/** Problems filed under the row they belong to, for rendering against it. */
function problemsById(
  rows: CustomMarkerDefinitionRow[],
  problems: DefinitionRowProblem[],
): { [rowId: string]: string[] } {
  const byId: { [rowId: string]: string[] } = {};
  problems.forEach(({ index, message }) => {
    const id = rows[index].id;
    byId[id] = [...(byId[id] || []), message];
  });
  return byId;
}

/** The problems on each row, keyed by row id for rendering against the row. */
export function definitionProblemsById(
  rows: CustomMarkerDefinitionRow[],
): { [rowId: string]: string[] } {
  return problemsById(rows, definitionRowProblems(rows));
}

/** Just the taken names, for the check the list runs on every keystroke. */
export function duplicateProblemsById(
  rows: CustomMarkerDefinitionRow[],
): { [rowId: string]: string[] } {
  return problemsById(rows, duplicateNameProblems(rows));
}

type Props = {
  rows: CustomMarkerDefinitionRow[];
  onChange: (rows: CustomMarkerDefinitionRow[]) => void;
  /** Supplied by the caller so this component stays free of id generation. */
  newRowId: () => string;
  /**
   * Whether this practitioner may ADD markers
   * (UserProfile.canUseCustomMarkers). When false the add control stays visible
   * but locked; definitions already saved remain listed and editable.
   */
  customMarkersAllowed?: boolean;
  /** Called instead of adding when the feature is not enabled. */
  onUpgradeNeeded?: () => void;
  /** The practitioner's own report sections, for the Section dropdown. */
  sections?: CustomMarkerSection[];
  /** Saves a section created from the Section dropdown to the profile. */
  onCreateSection?: (name: string) => void;
  /** Corrects a section's name in the list and on every definition using it. */
  onRenameSection?: (from: string, to: string) => void;
  /**
   * What Save found wrong, keyed by row id, rendered under the row it is about.
   * Save holds those rows back and commits the rest, so the message has to sit
   * where the practitioner can act on it rather than in a list at the bottom of
   * a table that can run to a hundred rows.
   */
  rowErrors?: { [rowId: string]: string[] };
  /**
   * The sections whose markers load onto every new test
   * (UserProfile.defaultTestSections). Undefined leaves the control off
   * entirely, for anywhere this list is rendered outside the profile.
   */
  defaultSections?: string[];
  /** Turns a section's "on every new test" setting on or off. */
  onToggleDefaultSection?: (sectionValue: string, on: boolean) => void;
  /** Individual markers set to load onto every new test, by definition id. */
  defaultMarkerIds?: string[];
  /** Turns one marker's "on every new test" setting on or off. */
  onToggleDefaultMarker?: (definitionId: string, on: boolean) => void;
  /** Sections whose markers show their ranges on Add Test Results. */
  expandedSections?: string[];
  /** Turns a section's "show ranges on tests" setting on or off. */
  onToggleExpandedSection?: (sectionValue: string, on: boolean) => void;
};

/**
 * Correct a section's name on every definition row filed under it. The rows
 * store the name, like the markers on a test do, so a rename that skipped them
 * would strand them under a section that no longer exists.
 */
export function renameInDefinitionRows(
  rows: CustomMarkerDefinitionRow[],
  from: string,
  to: string,
): CustomMarkerDefinitionRow[] {
  const fromKey = practitionerSectionKey(from);
  const next = practitionerSectionOptionValue(to);
  return (rows || []).map(row =>
    practitionerSectionKey(practitionerSectionFromOptionValue(row.section)) ===
    fromKey
      ? { ...row, section: next }
      : row,
  );
}

const RANGE_COLUMNS: {
  key: CustomMarkerRangeKey;
  label: string;
  required?: boolean;
}[] = [
  { key: 'alarmMin', label: 'Alarm Min' },
  { key: 'labMin', label: 'Lab Min', required: true },
  { key: 'optimalMin', label: 'Optimal Min' },
  { key: 'optimalMax', label: 'Optimal Max' },
  { key: 'labMax', label: 'Lab Max', required: true },
  { key: 'alarmMax', label: 'Alarm Max' },
];

// Sticky, because ten unlabeled input boxes in a row is what this table becomes
// the moment the header scrolls away. The wrapper below is what it sticks to.
const INPUT_STYLE: React.CSSProperties = { fontSize: '13px', height: '31px' };

/**
 * Rows past this many and the table scrolls inside its own box rather than with
 * the page, which is what gives the sticky header something to stick to: a
 * wrapper that only scrolls sideways is not a vertical scrollport, so `top: 0`
 * has nowhere to travel. Short lists keep the plain page scroll and lose
 * nothing — their header never leaves the screen anyway.
 */
const ROWS_BEFORE_TABLE_SCROLLS = 6;

/**
 * The "By sex" cell's summary of which variants a row has set.
 *
 * Named as the ranges that EXIST rather than as who is covered. "Both" on its
 * own is a word with no subject: sitting in an unheaded column two columns
 * from anything about sex, it read as a stray label rather than as an answer to
 * "which sexes have a range of their own". Reading the row across, "ng/mL · by
 * sex · female & male" now says what it is.
 *
 * Lower case, like the units and the range beside it: these are facts a row
 * states about itself, not column headers.
 *
 * A row with none reads "everyone", not "none": next to a chevron, "none" says
 * there is nothing here, when what is true is that one range covers everybody.
 * It is also the word the panel below already uses ("the ranges above for
 * everyone").
 */
export function bySexSummary(row: CustomMarkerDefinitionRow): string {
  if (row.female && row.male) {
    return 'female & male';
  }
  if (row.female) {
    return 'female';
  }
  if (row.male) {
    return 'male';
  }
  return 'everyone';
}

/**
 * What a marker row says about itself, as display text.
 *
 * Everything here is derived for READING. The row still stores its own units,
 * chart type and ranges; nothing about storage or the snapshot rule changes
 * because a fact got printed on a section heading instead of in a cell.
 */
export type RowFacts = {
  units: string;
  chartType: string;
  range: string;
  bySex: string;
};

/**
 * Which of the four facts a section may state once, rather than per marker.
 *
 * Down to one. Units, the range and the chart type are all as foldable as the
 * per-sex note, and folding any of them made one page describe its markers two
 * different ways: a panel that happened to agree printed the fact on the blue
 * band and nowhere else, while the panel under it printed it down a column, and
 * a practitioner comparing the two has to work out which arrangement they are
 * reading before they can read it. They are also what a marker is recognized
 * by, so each is worth a column of its own even when the column repeats itself.
 * Keeping them off the heading keeps it to one line as well, which is what the
 * section name, the count and five controls already fill.
 */
export type UniformFacts = {
  bySex?: string;
};

/**
 * The facts a heading is allowed to fold up, in the order it states them.
 *
 * The chart type is NOT among them, though a panel's markers usually agree on
 * it. Folded onto the heading it read as missing: a Glucose panel whose four
 * markers are all "high or low is a concern" said it once at the top and left
 * four rows blank in the column the panel beside it fills, which asks the
 * practitioner to look up at the heading to find out what the row under their
 * cursor actually is. Said on every row it is four short repeats, and a repeat
 * costs less than a gap that has to be explained.
 */
const FOLDABLE_FACTS: (keyof UniformFacts)[] = ['bySex'];

/**
 * The lab range as one phrase.
 *
 * Six boundary columns is what the practitioner MAINTAINS; two numbers is what
 * they recognize the marker by. Four of the six are calculated anyway, so
 * printing all six to be read is four columns of arithmetic pretending to be
 * data.
 *
 * A one-sided range is worded as the lab words it rather than padded out with
 * the bound LabSmarts derived: a mold marker's report says "< 1.9", not
 * "0 to 1.9".
 */
export function rangeSummary(row: CustomMarkerDefinitionRow): string {
  // A text marker has no range. What stands in its place is the expectation,
  // which is the fact a practitioner scanning the panel actually wants: it is
  // the only thing that decides whether a result gets marked on the report.
  if (isTextResult(row)) {
    const expected = (row.expectedText || '').trim();
    return expected ? `expected ${expected}` : '';
  }
  // Ranges set only by sex live on the variants, and the two of them cannot be
  // summed into one phrase.
  if (!hasLabBound(row.ranges) && (row.female || row.male)) {
    return 'by sex';
  }
  const min = (row.ranges?.labMin || '').trim();
  const max = (row.ranges?.labMax || '').trim();
  if (!min && !max) {
    return '';
  }
  // Named, because a bare "0 to 115" could be the lab's range, the optimal
  // window or the alarm bounds, and a practitioner reading a folded row has no
  // way to tell which. Prefixed rather than labeled to the right: it rides in
  // front of the numbers in one short word instead of repeating a phrase down
  // every row of a fourteen-marker panel.
  //
  // Lower case to sit in the row's list of facts ("ug/mL · lab up to 1.9")
  // without shouting.
  if (row.chartType === CustomMarkerChartType.HIGHER_IS_BETTER && !max) {
    return `lab ${min} and above`;
  }
  if (row.chartType === CustomMarkerChartType.LOWER_IS_BETTER && (!min || Number(min) === 0)) {
    return `lab up to ${max}`;
  }
  return min && max ? `lab ${min} to ${max}` : `lab ${min || max}`;
}

/** The chart type as prose, for a heading or a row. */
export function chartTypeSummary(row: CustomMarkerDefinitionRow): string {
  // Not "no chart type": a folded row that says nothing about itself reads as
  // an ordinary charted marker, and the whole point of this one is that it is
  // not. Worded as what it IS rather than as what it lacks.
  if (isTextResult(row)) {
    return 'text marker';
  }
  const option = CUSTOM_MARKER_CHART_TYPE_OPTIONS.find(
    entry => entry.value === (row.chartType || CustomMarkerChartType.STANDARD),
  );
  // Lower-cased because it is read as part of a sentence, whether about one
  // marker ("ug/mL · lab up to 1.9 · only high is a concern") or about the
  // section that agrees on it ("14 markers · only high is a concern"), and
  // not as a column header.
  return option ? option.label.toLowerCase() : '';
}

export function factsFor(row: CustomMarkerDefinitionRow): RowFacts {
  return {
    units: (row.units || '').trim(),
    chartType: chartTypeSummary(row),
    range: rangeSummary(row),
    bySex: bySexSummary(row),
  };
}

/**
 * The foldable facts every marker in a section agrees on.
 *
 * What a whole panel agrees on is stated once on the heading and dropped from
 * the rows. Kept to the per-sex note now: see FOLDABLE_FACTS for why the chart
 * type is said on every row instead.
 *
 * Needs at least two markers: a lone marker agreeing with itself is not a fact
 * about a panel.
 *
 * "everyone" is deliberately never folded up. It is the absence of a per-sex
 * range rather than a fact about the panel, and a heading is not improved by
 * announcing that nothing special is going on.
 */
export function uniformFactsFor(
  rows: CustomMarkerDefinitionRow[],
): UniformFacts {
  if (!rows || rows.length < 2) {
    return {};
  }
  const all = rows.map(factsFor);
  const shared = (key: keyof RowFacts): string | undefined => {
    const first = all[0][key];
    return first && all.every(facts => facts[key] === first) ? first : undefined;
  };
  const bySex = shared('bySex');
  return { bySex: bySex === 'everyone' ? undefined : bySex };
}

/** The order facts read in, on a heading and on a row alike. */
const FACT_ORDER: (keyof RowFacts)[] = ['units', 'range', 'chartType', 'bySex'];

/** One fact as a row states it, or '' when the row has nothing to say. */
function textForFact(facts: RowFacts, key: keyof RowFacts): string {
  // "everyone" on a row is the same non-fact it is on a heading.
  if (key === 'bySex' && facts.bySex === 'everyone') {
    return '';
  }
  return facts[key];
}

/**
 * Which facts the ROWS of a section carry, being the ones the heading does not.
 *
 * Decided once for the whole section rather than per row, so the columns line
 * up down the panel. Per row, a marker with two facts and its neighbor with one
 * put their first fact in different places, and a column that moves is not a
 * column: the eye cannot run down it.
 *
 * Kept for reading ONE section in isolation. What the page draws is
 * `pageFactKeys` — see there for why a column has to survive the section that
 * has nothing to put in it.
 */
export function factKeysToShow(
  rows: CustomMarkerDefinitionRow[],
  uniform: UniformFacts,
): (keyof RowFacts)[] {
  const all = (rows || []).map(factsFor);
  const folded = new Set<string>(
    FOLDABLE_FACTS.filter(key => !!uniform[key]),
  );
  return FACT_ORDER.filter(key => !folded.has(key)).filter(key =>
    all.some(facts => !!textForFact(facts, key)),
  );
}

/**
 * The columns the whole LIST draws: every column any one section draws.
 *
 * Decided per section, the columns lined up down a panel and nowhere else. A
 * stool panel whose markers state no units (pH, and two text results) dropped
 * the units column and started with its range — directly under the units of the
 * mold panel above it, so "lab 6.9 to 7.2" sat in the column reading "ug/mL"
 * and the ranges themselves ran down two different lines of the page. Reading a
 * library is scanning ONE column, and a column that only holds still within a
 * section is not one.
 *
 * So a section with nothing for a column keeps the space and leaves it blank.
 * That is the cost, and it buys the only thing these columns are for.
 *
 * A column NO section draws is still not drawn: the union is over what each
 * section would show on its own, so a fact folded up onto the one heading on
 * the page (or absent from every marker on it) leaves no gap behind. There has
 * to be something to line up with before an empty column is worth its width.
 */
export function pageFactKeys(
  sections: { rows: CustomMarkerDefinitionRow[]; uniform: UniformFacts }[],
): (keyof RowFacts)[] {
  const drawn = new Set<string>(
    (sections || []).flatMap(section =>
      factKeysToShow(section.rows, section.uniform),
    ),
  );
  return FACT_ORDER.filter(key => drawn.has(key));
}

/**
 * A row's facts, one per column the page shows, blanks included.
 *
 * `uniform` is the row's OWN section: a fact folded up onto its heading is
 * blanked here rather than dropped, so the column survives for the sections
 * that still fill it.
 */
export function rowFactsToShow(
  row: CustomMarkerDefinitionRow,
  keys: (keyof RowFacts)[],
  uniform: UniformFacts = {},
): { key: keyof RowFacts; text: string }[] {
  const facts = factsFor(row);
  const folded = new Set<string>(FOLDABLE_FACTS.filter(key => !!uniform[key]));
  return keys.map(key => ({
    key,
    text: folded.has(key) ? '' : textForFact(facts, key),
  }));
}

type State = {
  /** Name/section filter. UI only: it never touches what is saved. */
  filter: string;
  /**
   * Which section headings are folded shut, keyed by sectionGroupKey. UI only,
   * and per visit: a practitioner who collapses the mold panel to get at the
   * one marker below it has not said anything about tomorrow.
   */
  collapsed: { [groupKey: string]: boolean };
  /**
   * The one marker whose editor is open, by row id.
   *
   * One at a time on purpose. Every marker being editable at once is what this
   * page used to be: fourteen rows of eleven bordered inputs, a hundred and
   * eighty-two controls for a panel whose markers differ only in their names.
   * A library is read far more than it is edited, and edited one marker at a
   * time when it is.
   */
  editingRowId?: string;
  /**
   * The marker waiting on a delete confirmation, by row id.
   *
   * By id rather than index: the confirmation outlives a render, and an index
   * points at whichever row has slid into that position by the time it is
   * answered.
   */
  pendingRemoveId?: string;
  /** The section whose "set for all" editor is open, by sectionGroupKey. */
  editingSectionKey?: string;
  /** The row being dragged by its handle, and the row it is currently over. */
  dragIndex?: number;
  dropIndex?: number;
  /** Draft values for that editor, kept out of the rows until it is applied. */
  sectionDraft?: {
    units: string;
    chartType: CustomMarkerChartType;
    /** True when the markers agreed on that chart type rather than defaulting. */
    chartTypeSeeded: boolean;
    ranges: CustomMarkerRangeStrings;
  };
};

/** The facts a section-wide edit writes onto every marker under it. */
const SECTION_FACT_WIDTHS: { [K in keyof RowFacts]: string } = {
  units: '92px',
  range: '132px',
  chartType: '176px',
  // Room for "female & male", which is what this column says at its longest.
  bySex: '104px',
};

/**
 * The one fact that can be a phrase rather than a number.
 *
 * Three of the four are as long as they will ever be ("pg/mL", "lab 0 to 115",
 * "only high is a concern"), but a text marker's expectation is whatever the
 * lab prints, and "expected NEGATIVE at any dilution" ran out of column while
 * the name beside it sat in a third of a row of white space. So this column
 * alone grows, taking the room the name column is capped out of. Its stated
 * width stays its MINIMUM, so a section of ordinary charted markers looks
 * exactly as it did.
 */
const GROWING_FACT: keyof RowFacts = 'range';

/**
 * The ⓘ beside "My Markers", matching the one on Add Test Results' own card.
 *
 * Same job, different subject: that one explains what happens to a marker
 * typed onto a test, this one explains what the library is for. Everything
 * here is read once and never again, which is exactly what a popover is for
 * and exactly what a paragraph on the page is not.
 */
export const CustomMarkerLibraryHeader: React.FC = () => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom-start"
    overlay={
      <Popover id="my-markers-help" className="custom-marker-help-popover">
        <Popover.Title as="h6">How My Markers works</Popover.Title>
        <Popover.Content>
          <p>
            Markers LabSmarts does not ship yet, defined <strong>once</strong>{' '}
            here with the range your lab reports. They become a pick list on Add
            Test Results, so entering the same panel on the next client is a
            matter of typing its results rather than defining it again.
          </p>
          <p>
            You do not have to start here. A marker you type straight onto a
            test is <strong>added to this list when you save it</strong>, so the
            list fills itself as you work and this page is where you go to tidy
            up what is already in it.
          </p>
          <p>
            Only <strong>Lab Min</strong> and <strong>Lab Max</strong> are
            needed. Optimal and Alarm are calculated at 1 and 3 standard
            deviations from the middle of the lab range, and shown in gray so
            you can see what will be used. Type in one to override it.
          </p>
          <p>
            <strong>Section</strong> decides where the marker charts. One of
            LabSmarts&rsquo; own sections files it among that body
            system&rsquo;s bar graphs and counts it in that section&rsquo;s
            color and tally; a section of your own prints after them under your
            name for it. What placement never does is change a{' '}
            <strong>pattern</strong> LabSmarts detects, which read only the
            markers LabSmarts ships.
          </p>
          <p>
            <strong>Set for all</strong> writes one unit, chart type, or
            reference range across every charted marker in a section at once.
            Fill in only the boxes you want changed: a box left blank leaves
            that marker&rsquo;s own value alone, so correcting the lab max on
            fourteen markers changes nothing else about them. Text markers,
            names and sections are never touched, and nothing is written until
            you save the page.
          </p>
          <p>
            <strong>Drag a marker by the handle at its left</strong> to reorder
            it within its section, or select a handle and use the up and down
            arrow keys. That order is the order the markers are loaded onto a
            test in, and from there the order they are displayed in.
          </p>
          <p>
            <strong>Add to every new test</strong> loads a whole section, or a
            single marker, onto every test you add from now on. Add only the
            values you have. Anything left blank is not saved.
          </p>
          <p>
            <strong>Show ranges on tests</strong> keeps the full reference range
            in front of you while entering results, instead of folding each
            marker down to its name, result and units.
          </p>
          <p>
            Editing a marker here affects tests you add <strong>from now
            on</strong>. Tests you have already saved keep the ranges they were
            interpreted against, so a correction can never rewrite a report
            already given to a client.
          </p>
        </Popover.Content>
      </Popover>
    }
  >
    <span
      className="custom-marker-block-help"
      title="How My Markers works"
      onClick={(e: any) => e.stopPropagation()}
    >
      <FontAwesomeIcon icon="info-circle" />
    </span>
  </OverlayTrigger>
);

/**
 * My Markers (its own page at /my-markers, under the Test Results menu).
 * The practitioner's own marker definitions, entered once and picked onto tests
 * from Add Test Results.
 *
 * Read first, edit on request. The range RULES are shared with the per-test
 * table, not copied — see validateCustomMarkerRangeStrings.
 */
export class CustomMarkerLibrary extends React.Component<Props, State> {
  state: State = { filter: '', collapsed: {} };

  /**
   * The rows to draw, each with its index in the FULL list.
   *
   * Everything that edits a row addresses it by that index, so filtering can
   * only ever be about what is drawn: hand a renderer the index within the
   * filtered list and typing in the third visible row edits the third row of
   * the library.
   *
   * A row carrying a message is always drawn, filter or no filter — whether
   * the last Save held it back or its name clashes with another row's. The
   * message is the one thing on this page that has to be seen, and a filter
   * quietly hiding it is how a practitioner concludes the page saved when it
   * did not.
   */
  /**
   * Reorder within the section, by drag or by the handle's arrow keys.
   *
   * Library order is not decoration: it is the order the markers are picked
   * onto a test in ("Add all N" loads a whole panel in this order), and from
   * there the order they are charted in. A mold panel read down the lab report
   * it was typed from should stay in the lab report's order.
   */
  private moveRow = (fromIndex: number, toIndex: number, slots: number[]) => {
    if (!this.allowed) {
      return;
    }
    const next = moveRowWithin(this.props.rows, slots, fromIndex, toIndex);
    if (next !== this.props.rows) {
      this.props.onChange(next);
    }
  };

  private moveRowBy = (index: number, slots: number[], step: number) => {
    const ordered = [...slots].sort((a, b) => a - b);
    const target = ordered[ordered.indexOf(index) + step];
    if (!ordered.includes(index) || target === undefined) {
      return;
    }
    this.moveRow(index, target, ordered);
  };

  /**
   * The handle, in the gutter the row already has.
   *
   * Absolutely positioned into .custom-marker-line-head's 28px left padding
   * rather than added as another flex child: this row is read far more often
   * than it is reordered, and a control that pushes every name 20px right is
   * charging every reading for it. A row too short to reorder draws no handle
   * at all, and the padding is what keeps its names on the same x as everyone
   * else's.
   *
   * INSIDE the head, though positioned against it rather than laid out in it.
   * As a sibling of the head its only vertical anchor was the whole row, so it
   * was pinned a fixed distance from the top and lined up at exactly one row
   * height; anchored to the head it centers on the name whatever the row grows
   * to. The head's click opens the editor, so the handle stops its own click
   * going any further: a drag that opened an editor on release would make the
   * handle unusable.
   */
  private renderDragHandle(index: number, slots: number[]) {
    if (slots.length < 2 || !this.allowed) {
      return null;
    }
    return (
      <span
        className="custom-marker-drag-handle custom-marker-line-drag"
        role="button"
        tabIndex={0}
        draggable
        aria-label="Reorder this marker"
        title="Drag to reorder, or use the up and down arrow keys. This is the order the markers are picked onto a test in."
        onClick={e => e.stopPropagation()}
        onDragStart={e => {
          e.dataTransfer.effectAllowed = 'move';
          try {
            e.dataTransfer.setData('text/plain', String(index));
          } catch (err) {
            // IE-ism; the drag still works off component state.
          }
          const line = (e.currentTarget as HTMLElement).closest(
            '.custom-marker-line',
          );
          if (line && e.dataTransfer.setDragImage) {
            e.dataTransfer.setDragImage(line, 24, 16);
          }
          this.setState({ dragIndex: index, dropIndex: undefined });
        }}
        onDragEnd={() =>
          this.setState({ dragIndex: undefined, dropIndex: undefined })
        }
        onKeyDown={e => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.moveRowBy(index, slots, -1);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.moveRowBy(index, slots, 1);
          }
        }}
      />
    );
  }

  /** Where the dragged row would land, drawn on the row it would land against. */
  private dropClass(index: number): string {
    const { dragIndex, dropIndex } = this.state;
    if (dragIndex === undefined) {
      return '';
    }
    if (dragIndex === index) {
      return ' custom-marker-line-dragging';
    }
    if (dropIndex !== index) {
      return '';
    }
    return dragIndex < index
      ? ' custom-marker-line-drop-below'
      : ' custom-marker-line-drop-above';
  }

  private visibleRows(errors: {
    [rowId: string]: string[];
  }): { row: CustomMarkerDefinitionRow; index: number }[] {
    const filter = this.state.filter.trim().toLowerCase();
    return this.props.rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => {
        if (!filter || (errors[row.id] || []).length) {
          return true;
        }
        const section =
          practitionerSectionFromOptionValue(row.section) ||
          row.section ||
          CUSTOM_MARKER_OWN_SECTION_LABEL;
        return `${row.name} ${row.units} ${section}`
          .toLowerCase()
          .includes(filter);
      });
  }

  /**
   * What to say under each row: what the last Save refused, plus the one rule
   * checked as it is typed.
   *
   * A name already in the library was only ever reported by Save, which is a
   * long way from where it is made: the marker it clashes with is usually
   * folded into another section, so the page showed two identical rows and
   * said nothing until the button was pressed. It is also the only rule that
   * can be judged mid-form — every other one is about a field the practitioner
   * has simply not reached yet, and marking those red as they are filled in is
   * nagging.
   */
  private errorsByRow(): { [rowId: string]: string[] } {
    const merged: { [rowId: string]: string[] } = { ...(this.props.rowErrors || {}) };
    const live = duplicateProblemsById(this.props.rows);
    Object.keys(live).forEach(id => {
      // Save says it in the same words, so a row that has been saved since the
      // clash appeared would otherwise carry the message twice.
      merged[id] = Array.from(new Set([...(merged[id] || []), ...live[id]]));
    });
    return merged;
  }

  private update(index: number, patch: Partial<CustomMarkerDefinitionRow>) {
    this.props.onChange(
      this.props.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  private updateRange(
    index: number,
    set: 'ranges' | 'female' | 'male',
    key: CustomMarkerRangeKey,
    value: string,
  ) {
    const row = this.props.rows[index];
    const current = row[set] || emptyRangeStrings();
    this.update(index, { [set]: { ...current, [key]: value } } as any);
  }

  private toggleVariant(index: number, set: 'female' | 'male') {
    const row = this.props.rows[index];
    this.update(index, {
      [set]: row[set] ? undefined : emptyRangeStrings(),
    } as any);
  }

  /**
   * Empty the default range, leaving the per-sex ranges alone.
   *
   * Emptied rather than removed: `ranges` is not optional on the row, and the
   * six boxes have to stay on screen to be typed back into.
   */
  private clearDefaultRanges(index: number) {
    this.update(index, { ranges: emptyRangeStrings() });
  }

  private get allowed(): boolean {
    return this.props.customMarkersAllowed !== false;
  }

  /**
   * Add a blank row, optionally already filed into a section, and open it.
   *
   * Building a fourteen-marker panel is the case this page exists for, and
   * choosing the same section from a dropdown fourteen times is the part of it
   * nobody should have to do. The new row opens straight away because a blank
   * row has nothing to read.
   */
  private addRow = (
    sectionValue?: string,
    resultType: CustomMarkerResultType = CustomMarkerResultType.NUMERIC,
  ) => {
    // Locked: explain rather than add. The button stays clickable for exactly
    // this reason — a disabled one would swallow the click.
    if (!this.allowed) {
      if (this.props.onUpgradeNeeded) {
        this.props.onUpgradeNeeded();
      }
      return;
    }
    const id = this.props.newRowId();
    // A blank row matches no filter, so adding one under a filter would look
    // like the button doing nothing.
    this.setState(state => ({
      filter: '',
      editingRowId: id,
      // A row added to a folded section would be invisible, which reads as the
      // button doing nothing for a different reason.
      collapsed: sectionValue
        ? { ...state.collapsed, [sectionGroupKey(sectionValue)]: false }
        : state.collapsed,
    }));
    this.props.onChange([
      ...this.props.rows,
      {
        ...emptyDefinitionRow(id, resultType),
        ...(sectionValue ? { section: sectionValue } : {}),
      },
    ]);
  };

  private removeRow(index: number) {
    this.props.onChange(this.props.rows.filter((_, i) => i !== index));
  }

  /**
   * Ask first, unless there is nothing to lose.
   *
   * A marker is twelve typed fields, a chart shape, a section and often a pair
   * of per-sex ranges. Removing a SECTION, which is a name and nothing else,
   * has always opened a modal; removing the marker went straight through on one
   * click of a small icon, with no undo. The protection was on the cheaper
   * thing.
   *
   * A row nobody has typed into is still one click: a confirmation over an
   * empty row is a dialog asking permission to discard nothing.
   */
  private confirmRemove(index: number) {
    const row = this.props.rows[index];
    if (!row || isBlankDefinitionRow(row)) {
      this.removeRow(index);
      return;
    }
    this.setState({ pendingRemoveId: row.id });
  }

  /**
   * The confirmation itself. Modeled on My Sections' remove dialog, down to the
   * closing reassurance, because they are the same decision at two sizes and
   * two dialogs that phrase it differently teach that they do different things.
   */
  private renderRemoveConfirm() {
    const { pendingRemoveId } = this.state;
    const index = this.props.rows.findIndex(row => row.id === pendingRemoveId);
    const row = index >= 0 ? this.props.rows[index] : undefined;
    if (!row) {
      return null;
    }
    const name = row.name.trim();
    const close = () => this.setState({ pendingRemoveId: undefined });
    return (
      <Modal show onHide={close}>
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#337ab7', color: '#fff' }}
        >
          <Modal.Title style={{ fontSize: '18px' }}>
            Remove {name ? `“${name}”` : 'this marker'}?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '14px' }}>
          <p>
            It is taken out of My Markers, so it is no longer offered on Add
            Test Results and no longer loads onto new tests.
          </p>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: 0 }}>
            Tests you have already saved are left exactly as they are, and
            nothing is written until you save this page.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            style={{ color: '#6c757d' }}
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => {
              this.removeRow(index);
              close();
            }}
          >
            Remove this marker
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  /**
   * Open the "set for all" editor for a section, seeded with what its markers
   * already agree on.
   *
   * Seeded rather than blank because the common edit is a correction: the lab
   * revised the cut-off on a fourteen-marker panel, and every one of them moves
   * together. Where the markers disagree the field starts empty, so applying
   * without touching it cannot quietly flatten a difference that was deliberate.
   */
  private openSectionEditor(
    groupKey: string,
    rows: CustomMarkerDefinitionRow[],
  ) {
    if (!this.allowed) {
      if (this.props.onUpgradeNeeded) {
        this.props.onUpgradeNeeded();
      }
      return;
    }
    // Seeded from the CHARTED markers only, for the same reason applySectionDraft
    // skips the text ones: they have none of these fields, so counting them
    // would report a section as disagreeing about a chart type half its markers
    // do not have.
    const charted = rows.filter(row => !isTextResult(row));
    const agreed = <T,>(read: (row: CustomMarkerDefinitionRow) => T): T | undefined => {
      if (!charted.length) {
        return undefined;
      }
      const first = read(charted[0]);
      return charted.every(row => read(row) === first) ? first : undefined;
    };
    const ranges = emptyRangeStrings();
    CUSTOM_MARKER_RANGE_KEYS.forEach(key => {
      ranges[key] = agreed(row => (row.ranges || emptyRangeStrings())[key] || '') || '';
    });
    this.setState({
      editingSectionKey: groupKey,
      editingRowId: undefined,
      sectionDraft: {
        units: agreed(row => (row.units || '').trim()) || '',
        chartType:
          agreed(row => row.chartType) || CustomMarkerChartType.STANDARD,
        // Whether that chart type came from the markers or is just the
        // dropdown's floor. See applySectionDraft.
        chartTypeSeeded: agreed(row => row.chartType) !== undefined,
        ranges,
      },
    });
  }

  /**
   * Write the draft onto every marker in the section.
   *
   * Only the fields that were filled in. A blank one leaves each marker's own
   * value alone, which is what makes the seeded editor safe on a section whose
   * markers legitimately differ: clearing a field is not how you clear a value,
   * it is how you say "leave this one as it is".
   *
   * Names and section placement are never touched — those are what tell the
   * markers apart.
   */
  private applySectionDraft(indexes: number[], seeded: boolean) {
    const draft = this.state.sectionDraft;
    if (!draft) {
      return;
    }
    const targets = new Set(indexes);
    this.props.onChange(
      this.props.rows.map((row, index) => {
        if (!targets.has(index)) {
          return row;
        }
        // A text marker in the section is left alone entirely: units, chart
        // type and boundaries are the three things this editor writes, and it
        // has none of them. Being swept along by a "set for all" aimed at its
        // charted neighbors is how one would silently acquire a lab range it
        // can never be charted against.
        if (isTextResult(row)) {
          return row;
        }
        const next: CustomMarkerDefinitionRow = { ...row, ranges: { ...row.ranges } };
        if (draft.units.trim()) {
          next.units = draft.units.trim();
        }
        // Only when the section HAD one to seed from, or the practitioner
        // changed it. A <select> cannot be blank, so a section whose markers
        // disagree seeds at Standard — writing that unasked is how a "set for
        // all" done to fix Lab Max silently flattens every "only high is a
        // concern" marker in the panel.
        if (seeded || draft.chartType !== CustomMarkerChartType.STANDARD) {
          next.chartType = draft.chartType;
        }
        CUSTOM_MARKER_RANGE_KEYS.forEach(key => {
          const typed = (draft.ranges[key] || '').trim();
          if (typed) {
            next.ranges[key] = typed;
          }
        });
        return next;
      }),
    );
    this.setState({ editingSectionKey: undefined, sectionDraft: undefined });
  }

  /**
   * A boundary input that previews what LabSmarts will calculate when it is
   * left blank — the same shaded, italic grey cell the per-test table uses, so
   * the ranges are visible here before a test is ever entered.
   */
  private renderRangeInput(
    index: number,
    set: 'ranges' | 'female' | 'male',
    strings: CustomMarkerRangeStrings | undefined,
    row: CustomMarkerDefinitionRow,
    col: { key: CustomMarkerRangeKey; label: string },
    extraStyle?: React.CSSProperties,
  ) {
    const typed = strings ? strings[col.key] : '';
    const derived =
      strings && isCalculatedRangeKey(col.key, row.chartType)
        ? derivedRangeValue(
            strings,
            row.chartType,
            precisionForDefinitionRow(row),
            col.key,
          )
        : '';
    const isDerived = !(typed || '').trim() && !!derived;

    return (
      <Form.Control
        key={col.key}
        type="text"
        className={
          isDerived
            ? 'custom-marker-input custom-marker-derived'
            : 'custom-marker-input'
        }
        style={{ ...INPUT_STYLE, ...extraStyle }}
        disabled={!strings}
        value={typed || ''}
        placeholder={derived}
        title={
          isDerived
            ? `Calculated from the lab range. Type here to set your own ${col.label.toLowerCase()}.`
            : undefined
        }
        onChange={(e: any) =>
          this.updateRange(index, set, col.key, e.target.value)
        }
      />
    );
  }

  /**
   * The six boundary boxes under one label row, plus anything that acts on the
   * set as a whole.
   *
   * The trailing slot sits beside Alarm Max rather than under the grid, so an
   * action on these six boxes reads as part of the row it belongs to and not as
   * one more control in the stack of checkboxes below.
   */
  private renderRangeGrid(
    inputs: (col: { key: CustomMarkerRangeKey; label: string; required?: boolean }) => React.ReactNode,
    trailing?: React.ReactNode,
  ) {
    const grid = (
      <div className="custom-marker-range-grid">
        {RANGE_COLUMNS.map(col => (
          <div key={col.key}>
            <div className="custom-marker-field-label">
              {col.label}
              {col.required ? <RequiredMark /> : null}
            </div>
            {inputs(col)}
          </div>
        ))}
      </div>
    );
    if (!trailing) {
      return grid;
    }
    return (
      <div className="custom-marker-range-row">
        {grid}
        {trailing}
      </div>
    );
  }

  /**
   * The editor for one marker, opened from its row.
   *
   * Laid out as a labeled form rather than a line of bare boxes. Eleven
   * unlabeled inputs in a row is readable only against a header far above it,
   * which is the arrangement that made a fourteen-marker panel unreadable in
   * the first place.
   */
  private renderRowEditor(row: CustomMarkerDefinitionRow, index: number) {
    const isText = isTextResult(row);
    return (
      <div className="custom-marker-editor">
        <div className="custom-marker-editor-fields">
          <div style={{ flex: '2 1 220px' }}>
            <div className="custom-marker-field-label">
              Marker Name
              <RequiredMark />
            </div>
            <Form.Control
              type="text"
              className="custom-marker-input"
              style={INPUT_STYLE}
              value={row.name}
              maxLength={60}
              autoFocus={!row.name}
              placeholder={`e.g. ${
                isText
                  ? MARKER_NAME_PLACEHOLDER_TEXT_EXAMPLE
                  : MARKER_NAME_PLACEHOLDER_EXAMPLE
              }`}
              onChange={(e: any) => this.update(index, { name: e.target.value })}
            />
            {/* The same warning the per-test table shows, which this screen
                did not have at all. Defining "Glucose" here is worse than
                typing it on one test: it persists, it can be ticked onto every
                new test, and from then on every report carries a second
                Glucose bar on the practitioner's own range, outside every
                pattern LabSmarts detects. Nothing said so.

                A hint, not an error, for the reason it is one on the test
                form: the practitioner may have a marker the panels genuinely
                do not cover under a name LabSmarts happens to use. */}
            {shippedMarkerNamed(row.name, PanelField.PANEL_MARKER_FIELDS) ? (
              <div className="custom-marker-shipped-warning" style={{ maxWidth: '100%' }}>
                <FontAwesomeIcon icon="info-circle" />
                &nbsp;LabSmarts already has a field for this marker on Add
                Test Results, carrying the validated ranges, the root causes,
                and the patterns it feeds. Enter the result there rather than
                defining your own here, or the report will show it twice.
              </div>
            ) : null}
          </div>
          {/* A text marker has no units: the words are the result. The field
              is dropped rather than disabled here, because unlike the per-test
              table this is a form, not a grid — there is no column for a blank
              to hold open. */}
          {isText ? null : (
            <div style={{ flex: '0 1 110px' }}>
              {/* Optional: pH, a ratio and an index are reported as a bare
                  number, and LabSmarts ships a unitless marker itself (RPI).
                  A required field a marker legitimately cannot fill is how a
                  made-up unit ends up on a client's report. */}
              <div className="custom-marker-field-label">Units</div>
              <Form.Control
                type="text"
                className="custom-marker-input"
                style={INPUT_STYLE}
                value={row.units}
                maxLength={20}
                placeholder={`e.g. ${MARKER_UNITS_PLACEHOLDER_EXAMPLE}`}
                onChange={(e: any) => this.update(index, { units: e.target.value })}
              />
            </div>
          )}
          <div style={{ flex: '1 1 200px' }}>
            {/* No required mark: Chart Type defaults to Standard and validation
                never checks it, so a red asterisk here would be teaching
                practitioners to ignore the ones that mean something. */}
            <div className="custom-marker-field-label">
              Chart Type
              <ChartTypeHelp />
            </div>
            <Form.Control
              as="select"
              className="custom-marker-input"
              style={INPUT_STYLE}
              value={resultShapeValue(row)}
              onChange={(e: any) =>
                this.update(index, resultShapeFields(e.target.value))
              }
            >
              {CUSTOM_MARKER_RESULT_SHAPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          </div>
          {isText ? (
            <div style={{ flex: '1 1 200px' }}>
              <div className="custom-marker-field-label">
                Expected Result
              </div>
              <Form.Control
                type="text"
                className="custom-marker-input"
                style={INPUT_STYLE}
                value={row.expectedText}
                maxLength={CUSTOM_MARKER_EXPECTED_MAX_LENGTH}
                placeholder="e.g. Negative"
                title="Optional. Leave blank and the result is simply printed. Fill it in and a result that reads differently is marked on the report."
                onChange={(e: any) =>
                  this.update(index, { expectedText: e.target.value })
                }
              />
            </div>
          ) : null}
          <div style={{ flex: '1 1 200px' }}>
            {/* Still here, but only inside the editor. On every row it restated
                the heading directly above it, truncated, fourteen times. */}
            <div className="custom-marker-field-label">Section</div>
            <SectionSelect
              style={INPUT_STYLE}
              value={row.section}
              onChange={next => this.update(index, { section: next })}
              sections={this.props.sections}
              onCreateSection={this.props.onCreateSection}
              onRenameSection={this.props.onRenameSection}
            />
          </div>
        </div>

        {/* There is nothing to range a text result against, so the grid, its
            note and the per-sex overrides (which are per-sex RANGES) all drop
            out together. What replaces them is one sentence saying what this
            marker will do on a report, since a form with most of its fields
            missing otherwise reads as one that failed to load. */}
        {isText ? (
          <div className="custom-marker-editor-note" style={{ marginTop: '10px' }}>
            This marker&rsquo;s result is words, so it has no units and no
            reference range. It prints as a line of text in its section instead
            of a bar graph, and is left out of Results Overview, Markers Outside
            Optimal and the trend arrows. Set an <strong>Expected Result</strong>{' '}
            and any result that reads differently is marked on the report.
          </div>
        ) : (
          <>
        <div className="custom-marker-field-label" style={{ marginTop: '10px' }}>
          Reference range
        </div>
        {this.renderRangeGrid(
          col => this.renderRangeInput(index, 'ranges', row.ranges, row, col),
          // Only once both sexes cover everybody. Before that these numbers are
          // still the fallback for whoever has no variant, and a button
          // offering to clear a live range is a worse problem than the stale
          // one it was added to fix.
          defaultRangeIsRedundant(row) ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="custom-marker-range-clear"
              title="Empty these six boxes. The Female and Male ranges below now cover everybody, so nothing is read against them."
              onClick={() => this.clearDefaultRanges(index)}
            >
              Clear ranges
            </Button>
          ) : null,
        )}
        <div className="custom-marker-editor-note">
          Only Lab Min and Lab Max are needed. Optimal and Alarm are calculated
          when left blank, and Lab Min/Max too when you set ranges for both
          sexes.
        </div>

        <Form.Check
          type="checkbox"
          id={`by-sex-${row.id}`}
          className="custom-marker-editor-check"
          label="Different ranges by sex"
          checked={!!(row.female || row.male)}
          onChange={() =>
            this.props.onChange(
              this.props.rows.map((entry, i) =>
                i === index
                  ? row.female || row.male
                    ? { ...entry, female: undefined, male: undefined }
                    : { ...entry, female: emptyRangeStrings() }
                  : entry,
              ),
            )
          }
        />

        {row.female || row.male ? (
          <div className="custom-marker-variant-block">
            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '6px' }}>
              Leave a box unchecked to use the ranges above for everyone. Set{' '}
              <strong>both</strong> and the ranges above can stay empty, since
              there is nobody left for them to cover.
            </div>
            {GENDER_VARIANTS.map(({ key, label }) => {
              const strings = row[key];
              return (
                <div key={key} style={{ marginBottom: '8px' }}>
                  <Form.Check
                    type="checkbox"
                    id={`${row.id}-${key}`}
                    label={label}
                    style={{ fontSize: '13px', marginBottom: '4px' }}
                    checked={!!strings}
                    onChange={() => this.toggleVariant(index, key)}
                  />
                  {this.renderRangeGrid(col =>
                    this.renderRangeInput(index, key, strings, row, col),
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
          </>
        )}

        <div className="custom-marker-editor-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => this.setState({ editingRowId: undefined })}
          >
            Done
          </Button>
          <Button
            variant="link"
            size="sm"
            className="custom-marker-editor-remove"
            title="Remove this marker"
            onClick={() => {
              this.setState({ editingRowId: undefined });
              this.confirmRemove(index);
            }}
          >
            <FontAwesomeIcon icon="trash-alt" />
            &nbsp;Remove this marker
          </Button>
        </div>
      </div>
    );
  }

  /** The "set units and ranges for all N" editor, opened from a heading. */
  private renderSectionEditor(
    group: CustomMarkerSectionGroup<{ row: CustomMarkerDefinitionRow; index: number }>,
  ) {
    const draft = this.state.sectionDraft;
    if (!draft) {
      return null;
    }
    // Charted markers only, matching what openSectionEditor seeds from and what
    // applySectionDraft writes. Units, chart type and ranges are the fields a
    // text marker does not have, so counting them promised edits to markers
    // this editor leaves untouched.
    const count = group.items.filter(item => !isTextResult(item.row)).length;
    const setDraft = (patch: Partial<NonNullable<State['sectionDraft']>>) =>
      this.setState({ sectionDraft: { ...draft, ...patch } });

    return (
      <div className="custom-marker-editor custom-marker-section-editor">
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
          Set for all {count} markers in {group.label}
        </div>
        <div className="custom-marker-editor-fields">
          <div style={{ flex: '0 1 110px' }}>
            <div className="custom-marker-field-label">Units</div>
            <Form.Control
              type="text"
              className="custom-marker-input"
              style={INPUT_STYLE}
              value={draft.units}
              maxLength={20}
              placeholder="leave as is"
              onChange={(e: any) => setDraft({ units: e.target.value })}
            />
          </div>
          {/* `0 1`, so it does not GROW. This editor has only two fields on its
              line, so a growing chart type took every pixel the units box left
              and ran the width of the card — a dropdown five times wider than
              the same dropdown on a marker's own editor, which shares its line
              with three other fields. 220px is that field's basis, so the two
              read as the same control. */}
          <div style={{ flex: '0 1 220px' }}>
            <div className="custom-marker-field-label">
              Chart Type
              <ChartTypeHelp options={CUSTOM_MARKER_CHART_TYPE_OPTIONS} />
            </div>
            <Form.Control
              as="select"
              className="custom-marker-input"
              style={INPUT_STYLE}
              value={draft.chartType}
              onChange={(e: any) => setDraft({ chartType: e.target.value })}
            >
              {CUSTOM_MARKER_CHART_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className="custom-marker-field-label" style={{ marginTop: '10px' }}>
          Reference range
        </div>
        <div className="custom-marker-range-grid">
          {RANGE_COLUMNS.map(col => (
            <div key={col.key}>
              <div className="custom-marker-field-label">{col.label}</div>
              <Form.Control
                type="text"
                className="custom-marker-input"
                style={INPUT_STYLE}
                value={draft.ranges[col.key]}
                placeholder="leave as is"
                onChange={(e: any) =>
                  setDraft({
                    ranges: { ...draft.ranges, [col.key]: e.target.value },
                  })
                }
              />
            </div>
          ))}
        </div>
        <div className="custom-marker-editor-note">
          A box left blank leaves that marker&rsquo;s own value alone. Names and
          section stay as they are. Nothing is written until you save the page.
        </div>

        <div className="custom-marker-editor-actions">
          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              this.applySectionDraft(
                group.items.map(item => item.index),
                draft.chartTypeSeeded,
              )
            }
          >
            Apply to all {count} markers
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              this.setState({
                editingSectionKey: undefined,
                sectionDraft: undefined,
              })
            }
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  /** One marker, as a line to be read. */
  private renderRow(
    row: CustomMarkerDefinitionRow,
    index: number,
    factKeys: (keyof RowFacts)[],
    uniform: UniformFacts,
    editing: boolean,
    sectionDefaulted: boolean,
    sectionLabel: string,
    errors: string[],
    slots: number[],
  ) {
    const facts = rowFactsToShow(row, factKeys, uniform);
    // Only capped when the column that grows is actually drawn on the rows.
    // With nothing to absorb the width the cap gives up, a section whose
    // markers state no range at all (text results nobody wrote an expectation
    // on) would leave the gap AFTER its checkbox and pull the controls off the
    // right edge the other sections line up on.
    const capName = factKeys.indexOf(GROWING_FACT) >= 0;
    const { onToggleDefaultMarker } = this.props;
    const markerDefaulted = isDefaultTestMarker(
      this.props.defaultMarkerIds,
      row.id,
    );

    return (
      <div
        key={row.id}
        id={`my-marker-row-${row.id}`}
        className={
          (editing
            ? 'custom-marker-line custom-marker-line-editing'
            : 'custom-marker-line') + this.dropClass(index)
        }
        onDragOver={e => {
          if (this.state.dragIndex === undefined) {
            return;
          }
          // Without this the drop never fires.
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (this.state.dropIndex !== index) {
            this.setState({ dropIndex: index });
          }
        }}
        onDrop={e => {
          e.preventDefault();
          const { dragIndex } = this.state;
          this.setState({ dragIndex: undefined, dropIndex: undefined });
          if (dragIndex !== undefined) {
            this.moveRow(dragIndex, index, slots);
          }
        }}
      >
        {/* The WHOLE row opens the editor, not just the name.
            A name is a short target at the far left of a row whose only other
            controls are at the far right, and on a marker that states neither
            units nor a range there is nothing in between at all: the row reads
            as dead text. The name stays a real <button> inside it so
            the row is still reachable by keyboard and announced as a control;
            the trash stops the click going any further. */}
        <div
          className="custom-marker-line-head"
          onClick={() =>
            this.setState({
              editingRowId: editing ? undefined : row.id,
              editingSectionKey: undefined,
            })
          }
        >
          {this.renderDragHandle(index, slots)}
          <button
            type="button"
            className={
              capName
                ? 'custom-marker-line-name custom-marker-line-name-capped'
                : 'custom-marker-line-name'
            }
            title="Edit this marker"
          >
            {row.name.trim() || <em>Untitled marker</em>}
          </button>
          {facts.map(fact => (
            <span
              key={fact.key}
              className="custom-marker-line-fact"
              style={{
                flex: `${fact.key === GROWING_FACT ? '1 1' : '0 0'} ${
                  SECTION_FACT_WIDTHS[fact.key]
                }`,
              }}
              // Ellipsis is the last resort, not the answer: a long expectation
              // that still does not fit its widened column is readable on hover
              // rather than only by opening the editor.
              title={fact.text || undefined}
            >
              {fact.text}
            </span>
          ))}
          {/* One marker on every test, without ticking its whole section.
              A five-marker lipid panel where only NON HDL CHOLESTEROL is ever
              run has no section to tick, and ticking the section would bring
              the other four along.

              Shown ticked and locked while the SECTION is defaulted, because
              the marker is genuinely going onto every test and a box that read
              unticked would be lying about it. stopPropagation so the box does
              not also open the row it sits in. */}
          {onToggleDefaultMarker ? (
            <Form.Check
              type="checkbox"
              id={`default-marker-${row.id}`}
              className="custom-marker-line-default"
              // Labeled on every row rather than by a column header: a header
              // would push the section's own line in two, and the line is what
              // keeps six blue bands from becoming twelve. A bare checkbox with
              // nothing beside it said nothing at all.
              label="Add to every new test"
              title={
                sectionDefaulted
                  ? `On every new test because all of ${sectionLabel} is`
                  : 'Add this marker to every new test'
              }
              checked={sectionDefaulted || markerDefaulted}
              disabled={sectionDefaulted}
              onClick={(e: any) => e.stopPropagation()}
              onChange={(e: any) => {
                e.stopPropagation();
                onToggleDefaultMarker(row.id, !markerDefaulted);
              }}
            />
          ) : null}
          {/* The resting affordance. The row's hover state says it is live,
              but a page nobody has hovered yet has to show that its markers
              open, so this is drawn at all times rather than on hover. */}
          <span className="custom-marker-line-action" aria-hidden="true">
            <FontAwesomeIcon icon={editing ? 'chevron-up' : 'edit'} />
          </span>
          <Button
            variant="link"
            size="sm"
            className="custom-marker-line-action custom-marker-line-remove"
            title="Remove this marker"
            onClick={(e: any) => {
              e.stopPropagation();
              this.confirmRemove(index);
            }}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>

        {errors.length ? (
          <div className="custom-marker-line-errors">
            {errors.map(message => (
              <div key={message}>{message}</div>
            ))}
          </div>
        ) : null}

        {editing ? this.renderRowEditor(row, index) : null}
      </div>
    );
  }

  /**
   * A section heading: its name, its count, and everything its markers agree
   * on, said once.
   */
  private renderGroupHeading(
    group: CustomMarkerSectionGroup<{ row: CustomMarkerDefinitionRow; index: number }>,
    groupKey: string,
    folded: boolean,
    uniform: UniformFacts,
  ) {
    const {
      defaultSections,
      onToggleDefaultSection,
      expandedSections,
      onToggleExpandedSection,
    } = this.props;
    const count = group.items.length;
    // What "Set for all" can actually reach. On an all-text section the button
    // used to open an editor that accepted input, applied nothing and said
    // nothing; on a mixed one it named a count larger than it wrote to.
    const chartedCount = group.items.filter(item => !isTextResult(item.row))
      .length;
    const isDefault = isDefaultTestSection(defaultSections, group.value);
    const isExpanded = isExpandedTestSection(expandedSections, group.value);
    const shared = [uniform.bySex].filter(Boolean).join(' · ');

    return (
      <div className="custom-marker-group-heading">
        {/* ONE line, not two. The actions used to sit on a row of their own
            under the title, which doubled the height of every blue band on the
            page — six sections meant six double-height bands before a single
            marker was read. */}
        <div className="custom-marker-group-title">
          <Button
            variant="link"
            size="sm"
            className="custom-marker-group-toggle"
            title={folded ? `Show ${group.label}` : `Hide ${group.label}`}
            onClick={() =>
              this.setState(state => ({
                collapsed: { ...state.collapsed, [groupKey]: !folded },
              }))
            }
          >
            <FontAwesomeIcon
              icon={folded ? 'chevron-right' : 'chevron-down'}
              style={{ fontSize: '11px' }}
            />
            &nbsp;&nbsp;{group.label}
          </Button>
          <span className="custom-marker-group-count">
            {count} marker{count === 1 ? '' : 's'}
          </span>
          {shared ? (
            <span className="custom-marker-group-shared">{shared}</span>
          ) : null}

          {folded ? null : (
            <span className="custom-marker-group-actions">
              <Button
                variant="link"
                size="sm"
                className="custom-marker-group-add"
                title={`Add a numeric marker to ${group.label}`}
                onClick={() => this.addRow(group.value)}
              >
                + Add a numeric marker
              </Button>
              <Button
                variant="link"
                size="sm"
                className="custom-marker-group-add"
                title={`Add a marker the lab reports in words to ${group.label}`}
                onClick={() =>
                  this.addRow(group.value, CustomMarkerResultType.TEXT)
                }
              >
                + Add a text marker
              </Button>
              {chartedCount > 1 ? (
                <Button
                  variant="link"
                  size="sm"
                  className="custom-marker-group-add"
                  title={`Set the units, chart type and ranges on all ${chartedCount} markers at once`}
                  onClick={() =>
                    this.openSectionEditor(
                      groupKey,
                      group.items.map(item => item.row),
                    )
                  }
                >
                  Set for all {chartedCount}
                </Button>
              ) : null}
              {/* Only where a section can actually be resolved back to markers.
                  The appendix is every unfiled marker at once, which is not a
                  panel anybody runs, so it is not offered as a default. */}
              {onToggleDefaultSection && group.kind !== 'own' ? (
                <Form.Check
                  type="checkbox"
                  id={`default-section-${groupKey}`}
                  className="custom-marker-group-default"
                  // Named for its scope now that every row carries the plain
                  // wording: the header's box takes the whole panel, a row's
                  // box takes one marker.
                  label="Add whole section to every test"
                  title={`Load all of ${group.label} onto every test you add from now on. Markers you leave blank are not saved.`}
                  checked={isDefault}
                  onChange={() => onToggleDefaultSection(group.value, !isDefault)}
                />
              ) : null}
              {/* Folding a marker down to name, result and units suits a panel
                  entered from memory, and is wrong for one the practitioner
                  checks against the lab report as they type. Only they know
                  which theirs is, so it is asked rather than guessed. */}
              {onToggleExpandedSection ? (
                <Form.Check
                  type="checkbox"
                  id={`expanded-section-${groupKey}`}
                  className="custom-marker-group-default"
                  label="Show ranges on tests"
                  title={`Show the full reference range for every marker in ${group.label} on Add Test Results, instead of folding it away behind a caret.`}
                  checked={isExpanded}
                  onChange={() =>
                    onToggleExpandedSection(group.value, !isExpanded)
                  }
                />
              ) : null}
            </span>
          )}

          {/* Whose section this is, in the same place and the same voice as
              "YOUR SECTION" on the Add Test Results cards. Without it a
              practitioner cannot tell "Lipid Balance", which LabSmarts ships
              and prints its own way, from "Viral Antibodies", which they named
              and can rename or delete.

              The APPENDIX gets neither label, because both are wrong for it:
              the markers in it are theirs (it is named Practitioner-Added
              Markers), but the section is not — they did not name it, it is not
              in My Sections, and they cannot rename or delete it. "Your
              section" would promise management they do not have, and
              "LabSmarts section" reads as a denial of markers that are plainly
              theirs. Its own name already says what is in it, and the missing
              tag is itself the signal that it is not a section like the two
              kinds that carry one. */}
          {group.kind === 'own' ? null : (
            <span className="custom-marker-group-origin">
              {group.kind === 'practitioner'
                ? 'Your section'
                : 'LabSmarts section'}
            </span>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { rows } = this.props;
    const { filter } = this.state;
    const locked = !this.allowed;
    const errors = this.errorsByRow();
    const visible = this.visibleRows(errors);
    const filtering = !!filter.trim();
    // Grouped through the shared helper rather than sorted here, so My Markers,
    // the per-test table and the picker all put the sections in one order.
    // includeEmpty: a section named but not yet used still has to appear, or
    // there is nowhere to set it as a default or see that it exists.
    const groups = groupBySectionValue(
      visible,
      entry => entry.row.section,
      this.props.sections,
      { includeEmpty: !filtering },
    );
    // Per section first, because folding is a section's own business, then
    // unioned so a range sits in the range column whatever section it is in.
    // Under a filter it is the visible rows that have to line up, which is what
    // `groups` is already built from.
    const groupUniforms = groups.map(group =>
      uniformFactsFor(group.items.map(entry => entry.row)),
    );
    const factKeys = pageFactKeys(
      groups.map((group, i) => ({
        rows: group.items.map(entry => entry.row),
        uniform: groupUniforms[i],
      })),
    );

    return (
      // Same bottom margin the sections block carries, so the two are spaced
      // apart the way Report Settings spaces its blocks rather than My Sections
      // starting a line under the marker list's own footer.
      <div>
        {/* No heading of its own: the card around this is titled "My Markers",
            and repeating it inside is what made the two blocks on this page
            read as one to begin with. */}
        <p style={{ fontSize: '13px', color: '#212529', marginBottom: '10px' }}>
          Markers you use that LabSmarts does not cover yet, whether you define
          them here or type them onto a test and let them land here on save.
          They become a pick list on Add Test Results. Changing a marker here
          affects tests you add from now on; tests you have already saved keep
          the ranges they were interpreted against.
        </p>
        {rows.length > ROWS_BEFORE_TABLE_SCROLLS ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '10px',
            }}
          >
            <Form.Control
              type="text"
              className="custom-marker-input"
              style={{ ...INPUT_STYLE, maxWidth: '260px' }}
              placeholder="Filter by name or section"
              value={filter}
              onChange={(e: any) => this.setState({ filter: e.target.value })}
            />
            {filtering ? (
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                Showing {visible.length} of {rows.length}
              </span>
            ) : null}
          </div>
        ) : null}

        {rows.length === 0 && !groups.length ? (
          <p style={{ fontSize: '13px', color: '#868e96', marginBottom: '10px' }}>
            No markers yet.
          </p>
        ) : rows.length && visible.length === 0 ? (
          <p style={{ fontSize: '13px', color: '#868e96', marginBottom: '10px' }}>
            No markers match that filter.
          </p>
        ) : (
          <div>
            {groups.map((group, groupIndex) => {
              const groupKey = sectionGroupKey(group.value);
              // A section holding a flagged row stays open, for the same
              // reason the filter never hides one: the message is the one thing
              // on this page that has to be seen, and showFirstProblem cannot
              // scroll to a row that was never rendered.
              const held = group.items.some(
                entry => (errors[entry.row.id] || []).length > 0,
              );
              const folded = !held && !!this.state.collapsed[groupKey];
              const uniform = groupUniforms[groupIndex];
              const sectionDefaulted = isDefaultTestSection(
                this.props.defaultSections,
                group.value,
              );
              return (
                <div key={groupKey} className="custom-marker-group">
                  {this.renderGroupHeading(group, groupKey, folded, uniform)}
                  {!folded && this.state.editingSectionKey === groupKey
                    ? this.renderSectionEditor(group)
                    : null}
                  {folded
                    ? null
                    : group.items.map(({ row, index }, _i, items) =>
                        this.renderRow(
                          row,
                          index,
                          factKeys,
                          uniform,
                          // A flagged row opens itself: its fields are
                          // behind a caret now, and a message about a field
                          // nobody can see is a message nobody can act on.
                          this.state.editingRowId === row.id ||
                            (errors[row.id] || []).length > 0,
                          sectionDefaulted,
                          group.label,
                          errors[row.id] || [],
                          // Under a filter the rows between two visible ones are
                          // hidden, so a drop would move a marker somewhere the
                          // practitioner cannot see. No handles while filtering.
                          filtering ? [] : items.map(i => i.index),
                        ),
                      )}
                </div>
              );
            })}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '12px',
          }}
        >
          {/* Clickable while locked on purpose: the click is what explains why
              the feature is unavailable. */}
          <Button
            variant={locked ? 'outline-secondary' : 'outline-primary'}
            size="sm"
            onClick={() => this.addRow()}
            title={
              locked ? 'Custom markers are not enabled on this account' : undefined
            }
            style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            {locked ? <FontAwesomeIcon icon="lock" /> : null}
            {locked ? ' ' : null}+ Add a numeric marker
          </Button>
          {/* Its own button rather than a setting to find inside the new
              marker's Chart Type dropdown. A practitioner defining a urinalysis
              panel is looking for somewhere to put words, not for a chart
              setting, and a door nobody finds is a room nobody uses. */}
          <Button
            variant={locked ? 'outline-secondary' : 'outline-primary'}
            size="sm"
            onClick={() => this.addRow(undefined, CustomMarkerResultType.TEXT)}
            title={
              locked
                ? 'Custom markers are not enabled on this account'
                : 'For a marker the lab reports in words — a urinalysis color, “Negative”, “Not detected”'
            }
            style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            {locked ? <FontAwesomeIcon icon="lock" /> : null}
            {locked ? ' ' : null}+ Add a text marker
          </Button>
          {locked ? (
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              Adding your own markers is not enabled on this account.
            </span>
          ) : null}
        </div>
        {this.renderRemoveConfirm()}
      </div>
    );
  }
}
